'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import { AuthMode } from './AuthScreen';

interface ForgotFormData {
  email: string;
}

interface ForgotPasswordFormProps {
  onSwitch: (mode: AuthMode) => void;
}

export default function ForgotPasswordForm({ onSwitch }: ForgotPasswordFormProps) {
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<ForgotFormData>();

  // Backend integration point: POST /api/auth/forgot-password
  const onSubmit = async () => {
    await new Promise((r) => setTimeout(r, 1000));
    setSubmitted(true);
    toast.success('Password reset instructions sent.');
  };

  if (submitted) {
    return (
      <div className="animate-slide-up text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <Mail size={28} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Check your inbox</h2>
        <p className="text-muted-foreground text-sm mb-2">
          We sent password reset instructions to:
        </p>
        <p className="text-foreground font-medium text-sm mb-8">{getValues('email')}</p>
        <button
          onClick={() => onSwitch('login')}
          className="text-primary text-sm font-semibold hover:underline flex items-center gap-1.5 mx-auto"
        >
          <ArrowLeft size={14} />
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      <button
        onClick={() => onSwitch('login')}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
      >
        <ArrowLeft size={16} />
        Back to sign in
      </button>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1">Reset your password</h2>
        <p className="text-muted-foreground text-sm">
          Enter your university email and we'll send reset instructions.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        <div>
          <label htmlFor="forgot-email" className="block text-sm font-medium text-foreground mb-1.5">
            University email
          </label>
          <input
            id="forgot-email"
            type="email"
            autoComplete="email"
            placeholder="you@university.edu"
            className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
              errors.email ? 'border-destructive' : 'border-border'
            }`}
            {...register('email', {
              required: 'Email is required.',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-destructive">{errors.email.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          style={{ minHeight: '42px' }}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Sending...</span>
            </>
          ) : (
            'Send reset instructions'
          )}
        </button>
      </form>
    </div>
  );
}